import { ButtonHTMLAttributes, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'success'
  | 'outline'
  | 'link'
  | 'hunter'
  | 'gatherer';

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  gradient?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-primary-600 text-white border border-primary-600 hover:bg-primary-700 hover:border-primary-700 active:bg-primary-800 shadow-sm dark:shadow-dark-sm hover:shadow-md dark:hover:shadow-dark-md',
  secondary:
    'bg-surface-elevated text-text-primary border border-border hover:bg-surface-glass hover:border-border-hover shadow-sm dark:shadow-dark-sm hover:shadow-md dark:hover:shadow-dark-md',
  ghost:
    'bg-transparent text-text-primary border border-transparent hover:bg-surface hover:border-border',
  danger:
    'bg-error-600 text-white border border-error-600 hover:bg-error-700 hover:border-error-700 active:bg-error-800 shadow-sm dark:shadow-dark-sm hover:shadow-md dark:hover:shadow-dark-md',
  success:
    'bg-success-600 text-white border border-success-600 hover:bg-success-700 hover:border-success-700 active:bg-success-800 shadow-sm dark:shadow-dark-sm hover:shadow-md dark:hover:shadow-dark-md',
  outline:
    'bg-transparent text-text-primary border border-border hover:bg-surface hover:border-border-hover',
  link: 'bg-transparent text-primary-600 dark:text-primary-400 border border-transparent hover:text-primary-700 dark:hover:text-primary-300 hover:underline',
  hunter:
    'bg-hunter-600 text-white border border-hunter-600 hover:bg-hunter-700 hover:border-hunter-700 active:bg-hunter-800 shadow-sm dark:shadow-dark-sm hover:shadow-md dark:hover:shadow-dark-md hover:shadow-hunter-500/20',
  gatherer:
    'bg-gatherer-600 text-white border border-gatherer-600 hover:bg-gatherer-700 hover:border-gatherer-700 active:bg-gatherer-800 shadow-sm dark:shadow-dark-sm hover:shadow-md dark:hover:shadow-dark-md hover:shadow-gatherer-500/20',
};

const sizeClasses: Record<ButtonSize, string> = {
  xs: 'h-7 px-2 text-xs',
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-base',
  lg: 'h-12 px-6 text-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      gradient = false,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    // Apply gradient variant for primary, success, danger, hunter, gatherer
    const gradientClass = gradient
      ? variant === 'primary'
        ? 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800'
        : variant === 'success'
        ? 'bg-gradient-to-r from-success-600 to-success-700 hover:from-success-700 hover:to-success-800'
        : variant === 'danger'
        ? 'bg-gradient-to-r from-error-600 to-error-700 hover:from-error-700 hover:to-error-800'
        : variant === 'hunter'
        ? 'bg-gradient-to-r from-hunter-500 to-hunter-600 hover:from-hunter-600 hover:to-hunter-700'
        : variant === 'gatherer'
        ? 'bg-gradient-to-r from-gatherer-500 to-gatherer-600 hover:from-gatherer-600 hover:to-gatherer-700'
        : ''
      : '';

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={`
          inline-flex items-center justify-center gap-2
          rounded-lg font-medium
          transition-all duration-200 ease-in-out
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-bg
          disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none
          ${gradient ? gradientClass : variantClasses[variant]}
          ${sizeClasses[size]}
          ${className}
        `}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
